import {
    Box, Container, Divider,
    Grid, IconButton, Tooltip, Typography
} from "@material-ui/core";
import { createTheme, MuiThemeProvider } from "@material-ui/core/styles";
import {
    Add as AddIcon, BackspaceOutlined as BackspaceOutlinedIcon,
    Cancel as CancelIcon, Edit as EditIcon, Search as SearchIcon, Visibility as VisibilityIcon
} from "@material-ui/icons";
import ArrowCircleUpIcon from '@material-ui/icons/ArrowUpwardOutlined';
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import MUIDataTable from "mui-datatables";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import * as Yup from 'yup';
import CheckBox from "../../components/Form/Checkbox";
import DateField from "../../components/Form/Date";
import SelectPartNumber from "../../components/Form/SelectPartNumber";
import SelectSupplier from "../../components/Form/SelectSupplier";
import TextField from "../../components/Form/TextField";
import AlertDialog from "../../components/ui/AlertDialog";
import ButtonUI from "../../components/ui/ButtonUI";
import LoadingUI from "../../components/ui/LoadingUI";
import PageTitle from "../../components/ui/PageTitleUI";
import { useAuth } from "../../hooks/auth";
import { ShippingRequestSearch } from "../../interfaces/ShippingRequest";
import ShippingRequestApi from "../../services/ShippingRequestApi";
import getDefaultGridOptions from "../../utils/componentUtils";
import utilsDate from "../../utils/Date";
import permissions from "../../utils/Permissions";
import SelectStatus from "../Mvm/components/SelectStatus";
import { useStyles } from "./Style";

interface IFormInput {
    protocol: any;
    suppliers: [];
    selectedpartnumber: [];
    status: [];
    initialRequestDate: Date | null;
    endRequestDate: Date | null;
    invoiceKey: any;
}

const ShippingRequest: React.FC = () => {
    const history = useHistory();
    const classes = useStyles();
    const [shippingRequests, setShippingRequests] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const { user, hasPermission } = useAuth();
    const { t: tShippingRequest } = useTranslation("ShippingRequest");
    const { t: tVal } = useTranslation("Validation");
    const { t: _t } = useTranslation("General");
    const { t: tMvm } = useTranslation("Mvm");
    const { enqueueSnackbar } = useSnackbar();
    const [page, setPage] = useState(0);
    const [count, setCount] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [listSubmit, setListSubmit] = useState<number[]>([]);
    const [anyAbleSubmit, setAnyAbleSubmit] = useState<boolean>(false);
    const { control, handleSubmit, reset, getValues, setValue, clearErrors, setError } =
        useForm<IFormInput>({
            defaultValues: {
                protocol: "",
                suppliers: [],
                selectedpartnumber: [],
                status: [],
                initialRequestDate: null,
                endRequestDate: null,
                invoiceKey: "",
            },
        });
    
    const URL_REDIRECT = {
        view: "/ShippingRequest/View/",
        duplicate: "/ShippingRequest/Duplicate/",
        validate: "/ShippingRequest/Validate/",
        invoiceValidate: "/ShippingRequest/ValidateInvoice/",
        edit: "/ShippingRequest/Submit/",
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!hasPermission(permissions.SHIPPING_REQUEST.Search))
            history.replace('NoPermission');
    }, [history, user])

    const formValidation = async (data: any) => {
        var result = false
        clearErrors();

        try {
            const schema = Yup.object().shape({              
                initialRequestDate: Yup.date().nullable().typeError(tVal("formatDate")),
                endRequestDate: Yup.date().nullable().typeError(tVal("formatDate"))
            });

            await schema.validate(data, {
                abortEarly: false,
            });

            result = true
        }
        catch (err) {

            var errors: string[] = []

            if (err instanceof Yup.ValidationError) {
                err.inner.forEach(error => {

                    if (errors.filter(element => element.includes(error.message)).length === 0)
                        errors.push(error.message)

                    setError(error.path?.split('.')[0] as 
                    "initialRequestDate" | "endRequestDate"
                    , { message: error.message });
                })
            }

            result = false;
        }

        return result
    }

    const searchShippingRequest = async (data: IFormInput) => {
        
        var validForm = await formValidation(data)
        if (!validForm)
            return;

        setLoading(true);

        //Buscar por fornecedores do Usuario Fornecedor
        if (user.isSupplier && data.suppliers?.length === 0)
            data.suppliers = user.suppliers;

        let filter: ShippingRequestSearch = {
            companyId: +user.company.companyID,
            endRequestDate: data.endRequestDate,
            initialRequestDate: data.initialRequestDate,
            invoiceKey: data.invoiceKey,
            pageSize: rowsPerPage,
            pageIndex: page,
            plantId: Number(user.company.plantID),
            sort: "UpdatedAt",
            sortDir: "desc",
            supplierId: data.suppliers?.map((element: { code: any }) =>
                Number(element.code)
            ),
            protocolNumber: data.protocol,
        };

        if (data.selectedpartnumber != null &&
            data.selectedpartnumber.length > 0)
            filter.partNumberId = data.selectedpartnumber?.map((x: any) => x.code);

        if (data.status != null && data.status.length > 0)
            filter.status = data.status?.filter((x: any) => x.label !== 'Todos')
                .map((x : any) => x.code);

        ShippingRequestApi.search(filter)
            .then((result) => {
                if (result && result.items) {
                    setShippingRequests(result.items);
                    setCount(result.totalCount);
                    setPage(result.pageIndex);
                    setAnyAbleSubmit(
                        result.items.some(
                            (x: { status: string }) => x.status === "PMV"
                        )
                    );
                } else {
                    setShippingRequests([]);
                    setCount(0);
                    setPage(0);
                }
            })
            .catch(() => {
                setShippingRequests([]);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        setShippingRequests([]);
        searchShippingRequest(getValues());
    }, [user, page, rowsPerPage]);

    const onSubmit = (data: any) => {
        searchShippingRequest(data);
    };

    function handleDuplicate(id: number) {
        history.push(URL_REDIRECT.duplicate + id);
    }

    const columns = [
        {
            name: "id",
            options: {
                display: false,
            },
        },
        {
            name: "status",
            label: tMvm("createSubmitMvm"),
            options: {
                display: anyAbleSubmit,
                customBodyRender: (
                    value: any,
                    tableMeta: any,
                    updateValue: any
                ) => {
                    if (!anyAbleSubmit || value !== "PMV") return <></>;

                    return (
                        <CheckBox
                            onChange={(e) => {
                                if (e.target.checked)
                                    setListSubmit([
                                        ...listSubmit,
                                        tableMeta.rowData[0],
                                    ]);
                                else
                                    setListSubmit(
                                        listSubmit.filter(
                                            (e) => e !== tableMeta.rowData[0]
                                        )
                                    );
                            }}
                            label={""}
                            name={"checkSubmit" + tableMeta.rowData[0]}
                            control={control}
                        />
                    );
                },
            },
        },
        {
            name: "protocolNumber",
            label: String(tShippingRequest("protocol")),
            options: {
                sort: true,
                setCellHeaderProps: () => ({
                    align: "center",
                }),
                setCellProps: () => ({
                    align: "center",
                }),
            },
        },
        {
            name: "requestDate",
            label: String(tShippingRequest("requestDate")),
            options: {
                sort: true,
                setCellHeaderProps: () => ({
                    align: "center",
                }),
                setCellProps: () => ({
                    align: "center",
                }),
                customBodyRender: (
                    value: any,
                    tableMeta: any,
                    updateValue: any
                ) => {
                    return utilsDate.DateShort(value);
                },
            },
        },
        {
            name: "supplierId",
            label: String(tShippingRequest("supplierCode")),
            options: {
                sort: true,
                setCellHeaderProps: () => ({
                    align: "center",
                }),
                setCellProps: () => ({
                    align: "center",
                }),
            },
        },
        {
            name: "supplierCorporateName",
            label: String(tShippingRequest("companyName")),
            options: {
                sort: true,
                setCellHeaderProps: () => ({
                    align: "center",
                }),
                setCellProps: () => ({
                    align: "center",
                }),
            },
        },
        {
            name: "status",
            options: {
                display: false,
            },
        },
        {
            name: "statusDescription",
            label: String(tShippingRequest("status")),
            options: {
                sort: true,
                setCellHeaderProps: () => ({
                    align: "center",
                }),
                setCellProps: () => ({
                    align: "center",
                }),
            },
        },
        {
            name: "updatedBy",
            label: String(tShippingRequest("updatedBy")),
            options: {
                sort: true,
                setCellHeaderProps: () => ({
                    align: "center",
                }),
                setCellProps: () => ({
                    align: "center",
                }),
            },
        },
        {
            name: "updatedAt",
            label: String(tShippingRequest("updatedAt")),
            options: {
                sort: true,
                setCellHeaderProps: () => ({
                    align: "center",
                }),
                setCellProps: () => ({
                    align: "center",
                }),
                customBodyRender: (
                    value: any,
                    tableMeta: any,
                    updateValue: any
                ) => {
                    return utilsDate.DateTimeShort(value);
                },
            },
        },
        {
            name: "",
            options: {
                sort: false,
                setCellProps: () => ({
                    align: "right",
                }),
                customBodyRender: (
                    value: any,
                    tableMeta: any,
                    updateValue: any
                ) => renderViewButton(tableMeta.rowData[0]), // Id
            },
        },
        {
            name: "",
            options: {
                setCellProps: () => ({
                    align: "right",
                }),
                sort: false,
                customBodyRender: (
                    value: any,
                    tableMeta: any,
                    updateValue: any
                ) =>
                    renderEditButton(
                        // Id
                        tableMeta.rowData[0],
                        // Status
                        tableMeta.rowData[6]
                    ),
            },
        },
        {
            name: "",
            options: {
                setCellProps: () => ({
                    align: "right",
                }),
                sort: false,
                display: true,//user.isSupplier,
                customBodyRender: (
                    value: any,
                    tableMeta: any,
                    updateValue: any
                ) =>
                    renderValidateButton(
                        // Id
                        tableMeta.rowData[0],
                        // Status
                        tableMeta.rowData[6]
                    ),
            },
        },
        {
            name: "",
            options: {
                setCellProps: () => ({
                    align: "right",
                }),
                sort: false,
                // display: user.isSupplier,
                customBodyRender: (
                    value: any,
                    tableMeta: any,
                    updateValue: any
                ) =>
                    renderInvoiceValidateButton(
                        // Id
                        tableMeta.rowData[0],
                        // Status
                        tableMeta.rowData[6]
                    ),
            },
        },
        {
            name: "",
            options: {
                setCellProps: () => ({
                    align: "right",
                }),
                sort: false,
                customBodyRender: (
                    value: any,
                    tableMeta: any,
                    updateValue: any
                ) =>
                    renderButtonCancel(
                        // Id
                        tableMeta.rowData[0],
                        // Status
                        tableMeta.rowData[6],
                        // SupplierId
                        tableMeta.rowData[4]
                    ),
            },
        },
        {
            name: "",
            options: {
                sort: false,
                setCellProps: () => ({
                    align: "right",
                }),
                customBodyRender: (
                    value: any,
                    tableMeta: any,
                    updateValue: any
                ) => renderDuplicateButton(tableMeta.rowData[0]),
            },
        },
    ];

    const renderViewButton = (id: number) => {
        return (
            <>
                <Box display="flex">
                    <a href={URL_REDIRECT.view + id}>
                        <Tooltip title={String(_t("view"))}>
                            <IconButton
                                size="small"
                                edge="end"
                                aria-label="View"
                                color="primary"
                            >
                                <VisibilityIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </a>
                </Box>
            </>
        );
    };

    const renderDuplicateButton = (id: number) => {
        return (
            <>
                <Box display="flex">
                    <Tooltip title={String(tShippingRequest("duplicate"))}>
                        <IconButton
                            size="small"
                            edge="end"
                            aria-label="View"
                            color="primary"
                            onClick={() => handleDuplicate(id)}
                        >
                            <FileCopyIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </>
        );
    };

    const renderEditButton = (id: number, status: string) => {
        return (
            <>
                <Box display="flex">
                    <span>
                        <Tooltip title={String(_t("edit"))}>
                            <IconButton
                                size="small"
                                edge="end"
                                aria-label="Edit"
                                color="primary"
                                disabled={
                                    !(status === "RAS" || status === "PRV") ||
                                        !hasPermission(permissions.SHIPPING_REQUEST.Edit)
                                }
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </span>
                </Box>
            </>
        );
    };

    const renderValidateButton = (id: number, status: string) => {
        return (
            <>
                <Box display="flex">
                    <span>
                        <Tooltip title={String(_t("validate"))}>
                            <IconButton
                                size="small"
                                edge="end"
                                aria-label="Validate"
                                color="primary"
                                disabled={status !== "PAP" || 
                                    !hasPermission(permissions.SHIPPING_REQUEST.Validate)}
                            >
                                <CheckCircleIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </span>
                </Box>
            </>
        );
    };

    const renderInvoiceValidateButton = (id: number, status: string) => {
        return (
            <>
                <Box display="flex">
                    <span>
                        <Tooltip title={String(tShippingRequest(user.isSupplier ? "includeInvoice" : "invoiceManualValidate"))}>
                            <IconButton
                                size="small"
                                edge="end"
                                aria-label="Validate"
                                color="primary"
                                disabled={!(status === "PNF" || status === "PAN") || 
                                    !hasPermission(permissions.SHIPPING_REQUEST.InvoiceValidate)}
                            >
                                <ArrowCircleUpIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </span>
                </Box>
            </>
        );
    };

    const renderButtonCancel = (id: number, status: string, supplierId: number) => {
        return (
            <AlertDialog
                dialogTitle={_t("cancel")}
                dialogContentText={_t("dialogCancelContentText")}
                cancelTextButton={_t("cancelDeleteTextButton")}
                confirmTextButton={_t("confirmDeleteTextButton")}
                confirmDialogFunction={() => {
                    ShippingRequestApi.cancel(id).then(() => {
                        enqueueSnackbar(_t("returnCancelFormSuccess"), {
                            variant: "success",
                        });
                        searchShippingRequest(getValues());
                    });
                }}
                Button={
                    <IconButton
                        size="small"
                        color="primary"
                        edge="end"
                        disabled={
                            !user.isSupplier || 
                            !(user.suppliers.find((supplier : any) => supplier.code === supplierId)) ||
                            !(status === "CAN" || status === "RVM") ||
                            !hasPermission(permissions.SHIPPING_REQUEST.Delete)
                        }
                    >
                        <CancelIcon fontSize="small" />
                    </IconButton>
                }
            />
        );
    };

    const myTheme = createTheme({
        overrides: {
            MuiTableBody: {
                root: {
                    cursor: ""
                },
            },
        },
    });

    const prepareSubmitMvm = () => {
        if (anyAbleSubmit && listSubmit.length > 0)
            history.push(
                "/SubmitMvm?ShippingRequestIds=" + listSubmit.join(",")
            );
        else
            enqueueSnackbar(
                tShippingRequest("selectShippingRequestToContinue"),
                { variant: "warning" }
            );
    };

    return (
        <Container maxWidth={false} component="main">
            <PageTitle title={tShippingRequest("shippingRequest")} />
            <div className={classes.rootContent}>
                <div className={classes.paper}>
                    <form onSubmit={handleSubmit(onSubmit)}
                     style={{"marginBottom": "0.5rem"}}>
                        <Grid container spacing={1}>
                            <Grid
                                item
                                xs={6}
                                style={{
                                    padding: "0px",
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                <Typography className={classes.root}>
                                    {tShippingRequest("titleSearch")}
                                </Typography>
                            </Grid>

                            <Grid
                                item
                                xs={6}
                                style={{ padding: "0px", textAlign: "right", }}
                            >
                                <ButtonUI
                                    disabled={!hasPermission(permissions.SHIPPING_REQUEST.Add)}
                                    onClick={() => prepareSubmitMvm()}
                                    startIcon={<AddIcon />}
                                >
                                    {tMvm("submitMvm")}
                                </ButtonUI>
                                <ButtonUI
                                    onClick={() => reset()}
                                    startIcon={<BackspaceOutlinedIcon />}
                                >
                                    {tShippingRequest("clean")}
                                </ButtonUI>
                                <ButtonUI
                                    type="submit"
                                    color="primary"
                                    startIcon={<SearchIcon />}
                                >
                                    {tShippingRequest("search")}
                                </ButtonUI>
                            </Grid>
                        </Grid>

                        <Divider style={{ marginTop: "10px" }} />

                        <Grid container spacing={1}>
                            <Grid item xs={2}>
                                <TextField
                                    label={tShippingRequest("protocol")}
                                    name="protocol"
                                    control={control}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={2}>
                                <SelectSupplier
                                    label={tShippingRequest("supplier")}
                                    name="suppliers"
                                    control={control}
                                    multiple={true}
                                    setValue={setValue}
                                    loadUserSupplier={true}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={2}>
                                <SelectPartNumber
                                    label={tShippingRequest("design")}
                                    name="selectedpartnumber"
                                    control={control}
                                    multiple={true}
                                />
                            </Grid>
                            <Grid item xs={2}>
                                <SelectStatus
                                    name="status"
                                    context="AEStatusRequisicao"
                                    label={tShippingRequest("status")}
                                    control={control}
                                    multiple={true}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    label={tShippingRequest("invoiceKey")}
                                    name="invoiceKey"
                                    control={control}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item>
                                <DateField 
                                    label={tShippingRequest("requestDateBegin")}
                                    name="initialRequestDate"
                                    InputLabelProps={{shrink:true}}
                                    control={control}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item>
                                <DateField
                                    label={tShippingRequest("requestDateEnd")}
                                    name="endRequestDate"
                                    InputLabelProps={{shrink:true}}
                                    control={control}
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                    </form>

                    {loading ? (
                        <LoadingUI />
                    ) : (
                        <MuiThemeProvider theme={myTheme}>
                            <MUIDataTable
                                title=""
                                data={shippingRequests}
                                columns={columns}
                                options={{
                                    ...getDefaultGridOptions(_t),
                                    serverSide: true,
                                    pagination: true,
                                    sort: false,
                                    selectToolbarPlacement: "none",
                                    filter: false,
                                    search: false,
                                    download: false,
                                    print: false,
                                    viewColumns: false,
                                    selectableRows: "none",
                                    page,
                                    count,
                                    rowsPerPage,
                                    onChangePage: (pageIndex) => {
                                        setPage(pageIndex);
                                    },
                                    onChangeRowsPerPage: (rowsPerPage) => {
                                        setPage(0);
                                        setRowsPerPage(rowsPerPage);
                                    },
                                    onCellClick: (
                                        _colData,
                                        { colIndex, dataIndex }
                                    ) => {
                                        // Lógica para Click na Célula
                                    },
                                }}
                            />
                        </MuiThemeProvider>
                    )}
                </div>
            </div>
        </Container>
    );
};
export default ShippingRequest;
